import React from 'react';
import {
  Box,
  Container,
  Typography,
  Grid,
  Card,
  CardContent,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  styled,
} from '@mui/material';
import {
  BusinessCenter,
  AccountBalance,
  Receipt,
  EmojiObjects,
  CompareArrowsTwoTone,
  TrackChanges,
  SensorOccupied,
  CropSquare,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';

const SectionContainer = styled(Box)(({ theme }) => ({
  backgroundColor: '#f8f9fa',
  padding: theme.spacing(8, 0),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(6, 0),
  },
}));

const StyledCard = styled(Card)(({ theme }) => ({
  height: '100%',
  boxShadow: '0 4px 20px rgba(0, 0, 0, 0.1)',
  borderRadius: theme.spacing(2),
  transition: 'all 0.3s ease-in-out',
  border: '1px solid rgba(168, 85, 144, 0.1)',
  '&:hover': {
    transform: 'translateY(-5px)',
    boxShadow: '0 8px 30px rgba(168, 85, 144, 0.2)',
  },
}));

const ServiceCard = styled(StyledCard)(() => ({
  background: 'linear-gradient(135deg, #ffffff 0%, #f8f9fa 100%)',
}));

const ValuesCard = styled(StyledCard)(() => ({
  background: 'linear-gradient(135deg, #A85590 0%, #8B4A7A 100%)',
  color: '#ffffff',
}));

const SectionTitle = styled(Typography)(({ theme }) => ({
  color: '#A85590',
  fontWeight: 700,
  fontSize: '2.5rem',
  marginBottom: theme.spacing(2),
  textAlign: 'center',
  fontFamily: '"Playfair Display", "Georgia", serif',
  [theme.breakpoints.down('md')]: {
    fontSize: '2rem',
  },
}));

const ServiceTitle = styled(Typography)(({ theme }) => ({
  color: '#A85590',
  fontWeight: 600,
  fontSize: '1.5rem',
  marginBottom: theme.spacing(2),
  fontFamily: '"Playfair Display", "Georgia", serif',
}));

const ServiceDescription = styled(Typography)(({ theme }) => ({
  color: '#555555',
  fontSize: '1rem',
  lineHeight: 1.6,
  marginBottom: theme.spacing(2),
}));

const ValueItem = styled(ListItem)(({ theme }) => ({
  alignItems: 'flex-start',
  padding: theme.spacing(1.5, 0),
}));

const ValueTitle = styled(Typography)(({ theme }) => ({
  fontWeight: 600,
  fontSize: '1.1rem',
  marginBottom: theme.spacing(0.5),
  color: '#ffffff',
}));

const ValueDescription = styled(Typography)(() => ({
  fontSize: '0.95rem',
  lineHeight: 1.5,
  color: 'rgba(255, 255, 255, 0.9)',
}));

const ClosingStatement = styled(Typography)(({ theme }) => ({
  fontSize: '1.2rem',
  lineHeight: 1.6,
  textAlign: 'center',
  color: '#444444',
  fontStyle: 'italic',
  marginTop: theme.spacing(4),
  padding: theme.spacing(3),
  backgroundColor: 'rgba(168, 85, 144, 0.05)',
  borderRadius: theme.spacing(2),
  border: '1px solid rgba(168, 85, 144, 0.1)',
}));

const IconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(168, 85, 144, 0.1)',
  borderRadius: '50%',
  width: 50,
  height: 50,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginBottom: theme.spacing(2),
  '& .MuiSvgIcon-root': {
    color: '#A85590',
    fontSize: '1.8rem',
  },
}));

const WhiteIconWrapper = styled(Box)(({ theme }) => ({
  backgroundColor: 'rgba(255, 255, 255, 0.2)',
  borderRadius: '50%',
  width: 40,
  height: 40,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  marginRight: theme.spacing(2),
  flexShrink: 0,
  '& .MuiSvgIcon-root': {
    color: '#ffffff',
    fontSize: '1.5rem',
  },
}));

const ServicesOverview = () => {
  const { t } = useTranslation();

  const services = [
    {
      icon: <BusinessCenter />,
      title: t('services.consulting.title'),
      description: t('services.consulting.description'),
    },
    {
      icon: <AccountBalance />,
      title: t('services.accounting.title'),
      description: t('services.accounting.description'),
    },
    {
      icon: <Receipt />,
      title: t('services.tax.title'),
      description: t('services.tax.description'),
    },
  ];

  const coreValues = [
    {
      icon: <CompareArrowsTwoTone />,
      title: t('services.values.integrity.title'),
      description: t('services.values.integrity.description'),
    },
    {
      icon: <TrackChanges />,
      title: t('services.values.precision.title'),
      description: t('services.values.precision.description'),
    },
    {
      icon: <SensorOccupied />,
      title: t('services.values.respect.title'),
      description: t('services.values.respect.description'),
    },
    {
      icon: <CropSquare />,
      title: t('services.values.transparency.title'),
      description: t('services.values.transparency.description'),
    },
    {
      icon: <EmojiObjects />,
      title: t('services.values.empowerment.title'),
      description: t('services.values.empowerment.description'),
    },
  ];

  return (
    <SectionContainer>
      <Container maxWidth="lg">
        <Box mb={8}>
          <SectionTitle variant="h2" as="h2">
            {t('services.overview.title')}
          </SectionTitle>

          <Grid container spacing={4}>
            {services.map((service) => (
              <Grid item xs={12} md={4} key={service.title}>
                <ServiceCard>
                  <CardContent sx={{ p: 4, textAlign: 'center' }}>
                    <IconWrapper>{service.icon}</IconWrapper>
                    <ServiceTitle variant="h5" as="h3">
                      {service.title}
                    </ServiceTitle>
                    <ServiceDescription>
                      {service.description}
                    </ServiceDescription>
                  </CardContent>
                </ServiceCard>
              </Grid>
            ))}
          </Grid>

          <ClosingStatement>{t('services.overview.closing')}</ClosingStatement>
        </Box>

        <Divider sx={{ my: 6, backgroundColor: 'rgba(168, 85, 144, 0.2)' }} />
        <Box>
          <SectionTitle variant="h2" as="h2">
            {t('services.values.core.title')}
          </SectionTitle>

          <Grid container spacing={4}>
            <Grid item xs={12}>
              <ValuesCard>
                <CardContent sx={{ p: 4 }}>
                  <List sx={{ p: 0 }}>
                    {coreValues.map((value, index) => (
                      <React.Fragment key={value.title}>
                        <ValueItem>
                          <ListItemIcon sx={{ minWidth: 'auto' }}>
                            <WhiteIconWrapper>{value.icon}</WhiteIconWrapper>
                          </ListItemIcon>
                          <ListItemText
                            primary={<ValueTitle>{value.title}</ValueTitle>}
                            secondary={
                              <ValueDescription>
                                {value.description}
                              </ValueDescription>
                            }
                          />
                        </ValueItem>
                        {index < coreValues.length - 1 && (
                          <Divider
                            sx={{
                              backgroundColor: 'rgba(255, 255, 255, 0.2)',
                              my: 1,
                            }}
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </List>
                </CardContent>
              </ValuesCard>
            </Grid>
          </Grid>
        </Box>
      </Container>
    </SectionContainer>
  );
};

export default ServicesOverview;
